import { Observable, timer, TimeoutError } from 'rxjs';
import { delayWhen, scan } from 'rxjs/operators';

// Matches the error text AppSync/DynamoDB produce when a request was REJECTED without being
// executed (throttling, rate limiting, temporary unavailability). Only these are safe to retry.
const transientRequestErrorPattern = /throttl|rate exceeded|too many requests|provisionedthroughput|service ?unavailable|temporarily unavailable/i;

function collectRequestErrorMessages(requestError: any): string[] {
  const errorMessages: string[] = [];
  if (requestError && requestError.message) {
    errorMessages.push(String(requestError.message));
  }
  const graphQLErrors =
    (requestError && (requestError.graphQLErrors || requestError.errors)) || [];
  graphQLErrors.forEach(graphQLError => {
    if (graphQLError && graphQLError.message) {
      errorMessages.push(String(graphQLError.message));
    }
    if (graphQLError && graphQLError.errorType) {
      errorMessages.push(String(graphQLError.errorType));
    }
  });
  if (requestError && requestError.networkError && requestError.networkError.message) {
    errorMessages.push(String(requestError.networkError.message));
  }
  return errorMessages;
}

// True only when the server provably rejected the request WITHOUT executing it. A TimeoutError
// is explicitly NOT retryable: the mutation may still be running server-side, and re-issuing a
// non-idempotent create would duplicate the patient / re-send the questionnaire. Unknown errors
// are treated the same way for the same reason — retry only when the request never ran.
export function isSafelyRetryableRequestError(requestError: any): boolean {
  if (!requestError || requestError instanceof TimeoutError) {
    return false;
  }
  const networkError = requestError.networkError;
  if (networkError && (networkError.statusCode === 429 || networkError.statusCode === 503)) {
    return true;
  }
  return collectRequestErrorMessages(requestError).some(errorMessage =>
    transientRequestErrorPattern.test(errorMessage)
  );
}

// retryWhen companion shared by the create-patient and send-questionnaire phases: resubscribe
// with linear backoff (base * attempt) while the error is safely retryable and the attempt
// budget lasts; otherwise rethrow so the caller's catchError records the row as a failure
// instead of dropping it.
export function retryTransientRequestErrors(
  maxRetryAttempts: number,
  retryBackoffBaseMs: number
): (requestErrors: Observable<any>) => Observable<number> {
  return (requestErrors: Observable<any>) =>
    requestErrors.pipe(
      scan((attemptCount: number, requestError: any) => {
        if (!isSafelyRetryableRequestError(requestError) || attemptCount >= maxRetryAttempts) {
          throw requestError;
        }
        return attemptCount + 1;
      }, 0),
      delayWhen((attemptCount: number) => timer(retryBackoffBaseMs * attemptCount))
    );
}

// Human-readable reason for a failed request, shared by the create and send failure summaries.
export function describeRequestError(requestError: any): string {
  if (requestError instanceof TimeoutError) {
    return 'Timed out';
  }
  if (
    requestError &&
    requestError.graphQLErrors &&
    requestError.graphQLErrors.length > 0 &&
    requestError.graphQLErrors[0].message
  ) {
    return requestError.graphQLErrors[0].message;
  }
  if (
    requestError &&
    requestError.errors &&
    requestError.errors.length > 0 &&
    requestError.errors[0].message
  ) {
    return requestError.errors[0].message;
  }
  if (requestError && requestError.message) {
    return requestError.message;
  }
  return 'Unknown error';
}
